import { 
  ArrowLeftOutlined
} from '@ant-design/icons';
import { 
  Button, 
  Layout, 
  theme 
} from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import logo from '../../assets/image/logoSeeWard.png';
import { selectTree } from '../../redux/context/selectors';
import { consolePathFor } from '../../redux/context/treeUtils';
import { useCurrentUser } from '../hooks/useCurrentUser';
import ContextBreadcrumb from './context/ContextBreadcrumb';
import UserMenu from './UserMenu';
import ContextSwitcher, { 
  useSwitcherHotkeys 
} from './context/ContextSwitcher';

const { Header, Content } = Layout;

/**
 * The standalone frame for `/profile`. The profile belongs to the person, not to a
 * workspace, so it gets no sidebar: just a top bar with the logo, the breadcrumb and
 * switcher (the way to move between organisation, entity and workspace), a Back button
 * and the account menu, over the page centred.
 */
export default function ProfileLayout() {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const tree = useSelector(selectTree) as any[] | null;
  const { canAccessPlatformAdmin } = useCurrentUser();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  useSwitcherHotkeys(setSwitcherOpen);

  // No history means the page was opened directly (a bookmark or a new tab), so `-1`
  // would leave the app. Fall back to the person's own console, else the platform console.
  const goBack = () => {
    if (window.history.state?.idx > 0) return navigate(-1);
    const root = (tree ?? []).find((n) => n.has_console);
    navigate((root && consolePathFor(root)) || (canAccessPlatformAdmin ? '/admin' : '/'));
  };

  return <Layout 
    style={{ minHeight: '100vh', background: token.colorBgLayout }}
  >
    <Header
      style={{
        height: 64,
        background: token.colorBgContainer,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 16
      }}
    >
      <Link to="/">
        <img 
          src={logo} 
          alt="Seeward" 
          style={{ display: 'block', height: 30, width: 'auto' }} /
        >
      </Link>
      <Button 
        type="text" 
        icon={<ArrowLeftOutlined />} 
        onClick={goBack}
      >Back</Button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ContextBreadcrumb onOpenSwitcher={() => setSwitcherOpen(true)} />
      </div>
      <UserMenu />
    </Header>
    <Content style={{ overflow: 'auto' }}>
      <div 
        style={{ maxWidth: 880, margin: '0 auto', padding: '28px 32px 48px' }}
      >
        <Outlet />
      </div>
    </Content>
    <ContextSwitcher 
      open={switcherOpen} 
      onClose={() => setSwitcherOpen(false)} /
    >
  </Layout>;
}
